import React, { useState, useEffect, useRef } from 'react';
import { Search, User, Users } from 'lucide-react';
import { normalizePatientNameCode } from '../../utils/nameNormalizer';
import { ordersApi } from '../../services/api';

const PatientAutocomplete = ({
    patientNameCode,
    onPatientNameCodeChange,
    onPatientSelect,
    clientId = null,
    className = "",
    placeholder = "Ex: JEAN-CLAUDE MARTIN, CODE-123, etc."
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const suggestionRef = useRef(null);
    const inputRef = useRef(null);

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm.length >= 2) {
                performSearch(searchTerm);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, clientId]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
                setShowSuggestions(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const performSearch = async (term) => {
        if (term.length < 2) {
            setSuggestions([]);
            return;
        }

        setIsLoading(true);
        try {
            const results = await ordersApi.searchPatients(term, clientId);
            setSuggestions(results || []);
            setShowSuggestions(true);
        } catch (error) {
            console.error('Erreur lors de la recherche de patients:', error);
            setSuggestions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePatientNameCodeChange = (e) => {
        const value = e.target.value;
        // Ne pas normaliser pendant la saisie - permet à l'utilisateur de taper librement
        onPatientNameCodeChange(value);

        // Trigger search when typing
        setSearchTerm(value);
    };

    const handleSuggestionClick = (patient) => {
        onPatientNameCodeChange(patient.displayName);
        setShowSuggestions(false);
        setSelectedIndex(-1);

        if (onPatientSelect) {
            onPatientSelect(patient);
        }
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev > 0 ? prev - 1 : suggestions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
                    handleSuggestionClick(suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setSelectedIndex(-1);
                break;
        }
    };

    const getPatientStatusText = (patient) => {
        if (patient.orderCount === 1) {
            return "1 commande";
        }
        return `${patient.orderCount} commandes`;
    };

    const isExistingPatient = patientNameCode && suggestions.some(
        patient => patient.displayName.toUpperCase() === patientNameCode.toUpperCase()
    );

    return (
        <div className={`relative ${className}`} ref={suggestionRef}>
            {/* Single Patient Name/Code Field */}
            <div className="relative">
                <label htmlFor="patientNameCode" className="block text-sm font-medium text-gray-300 mb-1">
                    Nom/code du patient <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        id="patientNameCode"
                        value={patientNameCode}
                        onChange={handlePatientNameCodeChange}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className="w-full px-3 py-2 pr-8 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        autoComplete="off"
                        required
                    />
                    <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                </div>
            </div>

            {/* Patient Status Indicator */}
            {patientNameCode && (
                <div className="mt-2 flex items-center">
                    {isExistingPatient ? (
                        <div className="flex items-center text-green-400 text-sm">
                            <Users className="h-4 w-4 mr-1" />
                            <span className="font-medium">Patient existant</span>
                        </div>
                    ) : (
                        <div className="flex items-center text-yellow-300 text-sm">
                            <User className="h-4 w-4 mr-1" />
                            <span className="font-medium">Nouveau patient</span>
                        </div>
                    )}
                </div>
            )}

            {/* Suggestions Dropdown */}
            {showSuggestions && (suggestions.length > 0 || isLoading) && (
                <div className="absolute z-50 w-full mt-1 glass-deep border border-white/10 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {isLoading ? (
                        <div className="px-3 py-2 text-center text-gray-400">
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
                                Recherche...
                            </div>
                        </div>
                    ) : suggestions.length > 0 ? (
                        <>
                            <div className="px-3 py-2 text-xs text-gray-400 bg-black/30 border-b border-white/10">
                                Patients existants
                            </div>
                            {suggestions.map((patient, index) => (
                                <button
                                    key={`${patient.patientNameCode}-${index}`}
                                    onClick={() => handleSuggestionClick(patient)}
                                    className={`w-full px-3 py-2 text-left hover:bg-white/15 focus:bg-white/15 focus:outline-none ${
                                        selectedIndex === index ? 'bg-yellow-600/20 text-yellow-300' : ''
                                    }`}
                                    type="button"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-medium text-gray-200">
                                                {patient.displayName}
                                            </div>
                                            <div className="text-sm text-gray-400">
                                                {getPatientStatusText(patient)}
                                            </div>
                                        </div>
                                        <Users className="h-4 w-4 text-gray-500" />
                                    </div>
                                </button>
                            ))}
                        </>
                    ) : (
                        <div className="px-3 py-2 text-center text-gray-400">
                            Aucun patient trouvé
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PatientAutocomplete;
